import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Footer() {
  const { t } = useTranslation();

  const quickLinks = [
    { href: '/', label: t('nav.home') },
    { href: '/#services', label: t('nav.services') },
    { href: '/#register', label: t('nav.register') },
    { href: '/blog', label: t('nav.blog') },
  ];

  const supportLinks = [
    { href: '/help-center', label: t('footer.helpCenter') },
    { href: '/help-center', label: t('footer.faq') },
    { href: 'https://wa.me/+201044553254', label: t('footer.contactUs') },
    { href: '/terms', label: t('footer.terms') },
    { href: '/privacy', label: t('footer.privacy') },
  ];

  const socialLinks = [
    { href: 'https://www.tiktok.com/@5vixofficial', icon: 'ri-tiktok-fill', hoverClass: 'hover:bg-pink-500' },
    { href: 'https://www.youtube.com/@5vix-c3p', icon: 'ri-youtube-fill', hoverClass: 'hover:bg-red-600' },
    { href: 'https://www.instagram.com/5vixofficial/', icon: 'ri-instagram-fill', hoverClass: 'hover:bg-pink-600' },
    { href: 'https://www.linkedin.com/in/5vix-software-12058b363/', icon: 'ri-linkedin-fill', hoverClass: 'hover:bg-blue-600' },
  ];

  return (
    <footer className="bg-foreground-950 text-background-50 pt-16 pb-8">
      <div className="w-full px-4 md:px-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div>
            <Link to="/" className="text-2xl font-heading font-bold text-background-50 mb-4 block cursor-pointer">
              5<span className="text-primary-400">vix</span>
            </Link>
            <p className="text-foreground-300 mb-6 text-sm leading-relaxed">
              {t('footer.description')}
            </p>
            <div className="flex flex-col gap-3 mb-6">
              <a href="https://wa.me/+201044553254" target="_blank" rel="nofollow noopener noreferrer" className="flex items-center gap-2 text-foreground-300 hover:text-background-50 transition-colors text-sm">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-whatsapp-line"></i>
                </div>
                +201044553254
              </a>
              <a href="tel:201044553254" rel="nofollow" className="flex items-center gap-2 text-foreground-300 hover:text-background-50 transition-colors text-sm">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line"></i>
                </div>
                01044553254
              </a>
            </div>
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.icon}
                  href={social.href}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className={`w-9 h-9 flex items-center justify-center bg-foreground-800 rounded-full ${social.hoverClass} transition-colors cursor-pointer`}
                >
                  <i className={`${social.icon} text-background-50 text-lg`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-sm font-semibold text-background-50 mb-5 uppercase tracking-wider">
              {t('footer.quickLinks')}
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-foreground-300 hover:text-background-50 transition-colors text-sm cursor-pointer">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="text-sm font-semibold text-background-50 mb-5 uppercase tracking-wider">
              {t('footer.support')}
            </h3>
            <ul className="space-y-3">
              {supportLinks.map((link) => (
                <li key={link.label}>
                  {link.href.startsWith('tel:') || link.href.startsWith('https://') ? (
                    <a href={link.href} target="_blank" rel="nofollow noopener noreferrer" className="text-foreground-300 hover:text-background-50 transition-colors text-sm">
                      {link.label}
                    </a>
                  ) : (
                    <Link to={link.href} className="text-foreground-300 hover:text-background-50 transition-colors text-sm cursor-pointer">
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-sm font-semibold text-background-50 mb-5 uppercase tracking-wider">
              {t('footer.newsletter')}
            </h3>
            <p className="text-foreground-300 mb-4 text-sm leading-relaxed">
              {t('footer.newsletterDesc')}
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const phoneInput = e.currentTarget.elements.namedItem('whatsapp_phone') as HTMLInputElement;
                const phone = phoneInput?.value?.trim();
                if (phone) {
                  const message = encodeURIComponent(
                    `مرحباً 5vix! أريد الاشتراك في التحديثات والنصائح عبر واتساب. رقم هاتفي هو: ${phone}`
                  );
                  window.open(`https://wa.me/+201044553254?text=${message}`, '_blank');
                }
              }}
              className="flex"
            >
              <input
                type="tel"
                name="whatsapp_phone"
                required
                pattern="[0-9]*"
                inputMode="numeric"
                onInput={(e) => {
                  e.currentTarget.value = e.currentTarget.value.replace(/[^0-9]/g, '');
                }}
                placeholder={t('footer.whatsappPlaceholder') || 'WhatsApp number...'}
                className="bg-foreground-800 text-background-50 placeholder:text-foreground-400 px-4 py-2.5 rounded-l-lg rtl:rounded-l-none rtl:rounded-r-lg focus:outline-none focus:ring-2 focus:ring-primary-500 flex-1 text-sm"
              />
              <button
                type="submit"
                className="bg-primary-500 text-background-50 px-4 py-2.5 rounded-r-lg rtl:rounded-r-none rtl:rounded-l-lg hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer text-sm font-semibold"
              >
                {t('footer.subscribe')}
              </button>
            </form>
          </div>
        </div>

        <div className="border-t border-foreground-800 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-foreground-400 text-sm">{t('footer.copyright')}</p>
          <div className="flex gap-6">
            <Link to="/terms" className="text-foreground-400 hover:text-background-50 transition-colors text-sm cursor-pointer">
              {t('footer.terms')}
            </Link>
            <Link to="/privacy" className="text-foreground-400 hover:text-background-50 transition-colors text-sm cursor-pointer">
              {t('footer.privacy')}
            </Link>
            <Link to="/privacy" className="text-foreground-400 hover:text-background-50 transition-colors text-sm cursor-pointer">
              {t('footer.cookies')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}